import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { upsellClass } from 'components/portal/upsell';
import { cn } from 'lib/utils';
import type { VocabWord } from 'modules/vocabulary';
import { translationOf } from '../words/WordCard';
import { MarkedContext } from '../words/WordRow';
import { sourceLabel } from '../words/WordStatus';

const faceBase =
  'relative flex min-h-[clamp(240px,calc(100dvh-20rem),380px)] flex-col items-center justify-center rounded-3xl border p-6 text-center shadow-[0_30px_60px_-36px_rgba(16,24,43,0.35)] md:rounded-[28px] md:p-9 ' +
  'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-200';

interface CardFaceProps {
  word: VocabWord;
  /** The plan includes translations; without it the back shows the saved sentence and an unlock button. */
  locked: boolean;
  onUpgrade: () => void;
}

/** The front: the word, where it was saved from, a prompt to recall the translation, and (desktop) the flip button. */
export function CardFront({ word, children }: Pick<CardFaceProps, 'word'> & { children?: React.ReactNode }) {
  const { t } = useTranslation();
  const source = sourceLabel(word);
  return (
    <div className={cn(faceBase, 'border-border bg-card')}>
      {source && <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-bold text-foreground/80">{source}</span>}
      <p className={cn('break-words font-display text-[44px] font-extrabold leading-none tracking-[-0.045em] text-foreground md:text-[56px]', source && 'mt-5')}>
        {word.word}
      </p>
      <p className="mt-5 text-sm text-muted-foreground">{t('flashcards.recall')}</p>
      {children}
    </div>
  );
}

/** The back: the translation (or its unlock), and the sentence the word came from with the word marked. */
export function CardBack({ word, locked, onUpgrade }: CardFaceProps) {
  const { t, i18n } = useTranslation();
  const source = sourceLabel(word);
  const translation = translationOf(word, i18n.language);
  const example = word.definition?.example || word.context;
  return (
    <div className={cn(faceBase, 'border-[#D8E88A] bg-[#F7FAE6] dark:border-primary/30 dark:bg-primary/10')}>
      {source && <span className="rounded-full bg-card px-2.5 py-1 text-xs font-bold text-foreground/80">{source}</span>}
      <p className={cn('break-words font-display text-[26px] font-extrabold leading-none tracking-[-0.045em] text-muted-foreground md:text-[34px]', source && 'mt-4')}>
        {word.word}
      </p>
      {translation ? (
        <p className="mt-3 break-words font-display text-[26px] font-extrabold leading-[1.1] tracking-[-0.04em] text-foreground md:text-[34px]">
          {translation}
        </p>
      ) : locked ? (
        <div className="mt-3 flex w-full max-w-[360px] flex-col items-center gap-2.5">
          <div aria-hidden="true" className="pointer-events-none w-full select-none rounded-xl border border-dashed border-border bg-card/70 px-4 py-3 text-lg font-bold text-foreground/70 blur-[5px]">
            ••••••• •••• ••••••
          </div>
          <button
            type="button"
            onClick={onUpgrade}
            className={cn(
              'inline-flex min-h-11 items-center gap-2 rounded-xl px-4 text-[13px] font-bold focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              upsellClass,
            )}
          >
            <Lock className="h-3.5 w-3.5" aria-hidden /> {t('flashcards.unlock_translation')}
          </button>
        </div>
      ) : null}
      {example && (
        <p className="mt-5 max-w-[520px] font-serif text-[15.5px] leading-[1.6] text-foreground/80 md:text-[17px]">
          <MarkedContext context={example} word={word.word} markClass="bg-[#FDE68A] dark:bg-amber-400/40" />
        </p>
      )}
    </div>
  );
}
